import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type Announcements,
  type DragEndEvent,
  type ScreenReaderInstructions,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import {
  CalendarDays,
  ChevronDown,
  GripVertical,
  ListTodo,
  Lock,
  Palette,
  Pencil,
  Pin,
  PinOff,
  Plus,
  StickyNote,
  Trash2,
  X,
} from 'lucide-react';
import { Suspense, lazy, useState, type ReactNode } from 'react';
import type { NoteEditorProps } from '../components/NoteEditor';
import { TagChip, TagChips, TagField } from '../components/NoteTags';
import { ColorChoice, NOTE_STYLE, NoteImages, NoteText } from '../components/NoteView';
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  LoadingBlock,
  PageHeader,
  useToast,
  type BadgeTone,
} from '../components/ui';
import { api } from '../lib/api';
import { formatDate, formatDateTime, relativeTime } from '../lib/format';
import { useI18n, type TranslationKey } from '../lib/i18n';
import {
  NOTES_KEY,
  TODO_TITLE_MAX,
  dayDate,
  dueState,
  noteLines,
  noteRefusal,
  sameTag,
  tagIdLists,
  type NoteTag,
  type NotesOverview,
  type StickyColor,
  type StickyNoteItem,
  type TodoItem,
} from '../lib/notes';

/**
 * My notes and My to-do list (operator, 2026-10-15: "i want to add to do list
 * and sticky notes private for everyone using the system to organize his work
 * in notes and to do tasks i must can tag @ task or ticket from the list who
 * assigned to me"; then "i need to be sprite tabs under My Notes My Tod Do
 * List" — two pages in the sidebar, one under the other, not two tabs on one).
 *
 * Each is the reader's own and nobody else's — administrators included. My notes
 * (`/notes`) is a board of sticky notes in six colours, pinned or not; My to-do
 * list (`/todos`) is ticked off line by line and dragged into the order the work
 * goes in. In both, @ tags a task, a Q Task or a ticket given to the reader,
 * which then shows as a chip that opens it, with its status.
 *
 * Like a task's own to-do list, everything is written in place and saved the
 * moment it is made — there is no form to open. A note is formatted, shows
 * pictures and saves itself as it is written (operator, 2026-11-01: "add this
 * options to notes and add auto save"; components/NoteEditor.tsx). Only
 * deleting a note and clearing the done lines ask first. Both pages read
 * `GET /notes`, which holds both lists, so going from one to the other shows it
 * at once.
 */

/**
 * The note editor — TipTap and ProseMirror, about 128 kB gzipped — loads the
 * first time a note is written or changed, not with the console.
 */
const NoteEditorChunk = lazy(() => import('../components/NoteEditor').then((m) => ({ default: m.NoteEditor })));

function NoteEditor(props: NoteEditorProps) {
  return (
    // As tall as the editor it stands in for: its words, its toolbar and its foot.
    <Suspense fallback={<div className={clsx('rounded-md', props.note ? 'h-[14rem]' : 'h-[10.5rem]')} aria-busy />}>
      <NoteEditorChunk {...props} />
    </Suspense>
  );
}

/** The reader's notes and to-do list, as `GET /notes` hands them out: one query for both pages. */
function useMyNotes() {
  return useQuery({ queryKey: NOTES_KEY, queryFn: () => api.get<NotesOverview>('/notes') });
}

/** Under a page's title: whose it is, with a lock. */
function OnlyYours({ children }: { children: ReactNode }) {
  return (
    <span className="inline-flex items-start gap-1.5">
      <Lock className="mt-[3px] h-3.5 w-3.5 shrink-0" aria-hidden />
      {children}
    </span>
  );
}

/** My notes, `/notes`: the board of sticky notes. */
export function NotesPage() {
  const { t } = useI18n();
  const notes = useMyNotes();
  return (
    <div>
      <PageHeader
        title={t('notes.title')}
        count={notes.data?.notes.length}
        subtitle={<OnlyYours>{t('notes.sticky.subtitle')}</OnlyYours>}
      />
      {notes.isLoading ? (
        <LoadingBlock />
      ) : notes.error || !notes.data ? (
        <ErrorState error={notes.error} onRetry={() => void notes.refetch()} />
      ) : (
        <StickyBoard notes={notes.data.notes} />
      )}
    </div>
  );
}

/** My to-do list, `/todos`: the open lines in their order, then the done ones. */
export function TodosPage() {
  const { t } = useI18n();
  const notes = useMyNotes();
  const todos = notes.data?.todos ?? [];
  const open = todos.filter((item) => !item.isDone).sort(byPlace);
  const done = todos.filter((item) => item.isDone).sort(byLatestDone);
  return (
    <div>
      <PageHeader
        title={t('notes.todo.title')}
        count={notes.data ? open.length : undefined}
        subtitle={<OnlyYours>{t('notes.todo.subtitle')}</OnlyYours>}
      />
      {notes.isLoading ? (
        <LoadingBlock />
      ) : notes.error || !notes.data ? (
        <ErrorState error={notes.error} onRetry={() => void notes.refetch()} />
      ) : (
        <TodoList open={open} done={done} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared
// ---------------------------------------------------------------------------

/** The list's own order: highest place first, as the server keeps it. */
const byPlace = (a: { position: number; createdAt: string }, b: { position: number; createdAt: string }) =>
  b.position - a.position || b.createdAt.localeCompare(a.createdAt);

/** Done lines, the latest ticked first. */
const byLatestDone = (a: TodoItem, b: TodoItem) => (b.doneAt ?? '').localeCompare(a.doneAt ?? '');

/**
 * A drag, as the server will apply it: the items named, in their new order,
 * take the places they hold between them; nothing else moves.
 */
function withPlaces<T extends { id: string; position: number }>(items: T[], ids: string[]): T[] {
  const named = new Set(ids);
  const places = items
    .filter((item) => named.has(item.id))
    .map((item) => item.position)
    .sort((a, b) => b - a);
  const place = new Map(ids.map((id, index) => [id, places[index]] as const));
  return items.map((item) => {
    const position = place.get(item.id);
    return position === undefined ? item : { ...item, position };
  });
}

/** Mouse and touch after a short move (a click stays a click), and the keyboard: Space, arrows, Space. */
function useDragSensors() {
  return useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
}

/**
 * What a screen reader says while a line or a note is dragged — its name and its
 * place, in the reader's language — instead of the drag library's English with
 * the record's id in it.
 */
function useDragWords(ids: string[], nameOf: (id: string) => string) {
  const { t } = useI18n();
  const place = (id: string | number) => ({ position: ids.indexOf(String(id)) + 1, total: ids.length });
  const announcements: Announcements = {
    onDragStart: ({ active }) => t('notes.drag.picked', { name: nameOf(String(active.id)) }),
    onDragOver: ({ active, over }) =>
      over ? t('notes.drag.over', { name: nameOf(String(active.id)), ...place(over.id) }) : undefined,
    onDragEnd: ({ active, over }) =>
      over
        ? t('notes.drag.dropped', { name: nameOf(String(active.id)), ...place(over.id) })
        : t('notes.drag.cancelled', { name: nameOf(String(active.id)) }),
    onDragCancel: ({ active }) => t('notes.drag.cancelled', { name: nameOf(String(active.id)) }),
  };
  const screenReaderInstructions: ScreenReaderInstructions = { draggable: t('notes.drag.instructions') };
  return { announcements, screenReaderInstructions };
}

/** Dragged into a new order: drawn at once, then saved; the page is read again either way. */
function useReorder(list: 'todos' | 'notes') {
  const { t } = useI18n();
  const toast = useToast();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => api.put(list === 'todos' ? '/notes/todos/order' : '/notes/stickies/order', { ids }),
    onMutate: async (ids) => {
      await queryClient.cancelQueries({ queryKey: NOTES_KEY });
      const previous = queryClient.getQueryData<NotesOverview>(NOTES_KEY);
      queryClient.setQueryData<NotesOverview>(NOTES_KEY, (old) =>
        !old
          ? old
          : list === 'todos'
            ? { ...old, todos: withPlaces(old.todos, ids) }
            : { ...old, notes: withPlaces(old.notes, ids) },
      );
      return { previous };
    },
    onError: (e: Error, _ids, context) => {
      if (context?.previous) queryClient.setQueryData(NOTES_KEY, context.previous);
      toast.error(noteRefusal(e, t));
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: NOTES_KEY }),
  });
}

/** The ids a drag ends with, or null when nothing moved. */
function droppedOrder(ids: string[], { active, over }: DragEndEvent): string[] | null {
  if (!over || active.id === over.id) return null;
  const from = ids.indexOf(String(active.id));
  const to = ids.indexOf(String(over.id));
  return from < 0 || to < 0 ? null : arrayMove(ids, from, to);
}

/** A small round button on a line or a note: edit it, take it off, pin it. */
function IconButton({
  label,
  onClick,
  disabled,
  expanded,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  expanded?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      aria-expanded={expanded}
      onClick={onClick}
      disabled={disabled}
      className="grid h-8 w-8 shrink-0 place-items-center rounded-full text-faint transition-colors duration-fast hover:bg-[var(--surface-raised)] hover:text-[var(--text-primary)] disabled:opacity-50"
    >
      {children}
    </button>
  );
}

/** The grip a line or a note is dragged by — Space picks it up from the keyboard. */
function DragHandle({ label, sortable }: { label: string; sortable: ReturnType<typeof useSortable> }) {
  return (
    <button
      type="button"
      ref={sortable.setActivatorNodeRef}
      {...sortable.attributes}
      {...sortable.listeners}
      aria-label={label}
      title={label}
      className="grid h-7 w-6 shrink-0 cursor-grab touch-none place-items-center rounded text-faint hover:text-[var(--text-primary)] active:cursor-grabbing"
    >
      <GripVertical className="h-4 w-4" aria-hidden />
    </button>
  );
}

const sortableStyle = (sortable: ReturnType<typeof useSortable>) => ({
  transform: CSS.Translate.toString(sortable.transform),
  transition: sortable.transition,
});

// ---------------------------------------------------------------------------
// The to-do list
// ---------------------------------------------------------------------------

interface TodoDraft {
  title: string;
  tags: NoteTag[];
  dueOn: string | null;
}

const todoBody = (draft: TodoDraft) => ({ title: draft.title, dueOn: draft.dueOn, ...tagIdLists(draft.tags) });

/** What a line is called where it has no words of its own: its tags. */
function todoName(item: TodoItem, t: (key: TranslationKey) => string): string {
  if (item.title) return item.title;
  const tagged = item.tags.filter((tag) => tag.available).map((tag) => tag.number ?? tag.title);
  return tagged.length ? tagged.join(', ') : t('notes.todo.label');
}

function TodoList({ open, done }: { open: TodoItem[]; done: TodoItem[] }) {
  const { t } = useI18n();
  const toast = useToast();
  const queryClient = useQueryClient();
  const sensors = useDragSensors();
  const reorder = useReorder('todos');
  // A new composer after each add — empty, and holding the focus for the next line.
  const [composer, setComposer] = useState(0);
  const [editing, setEditing] = useState<string | null>(null);
  const [showDone, setShowDone] = useState(true);
  const [confirmClear, setConfirmClear] = useState(false);
  const refresh = () => queryClient.invalidateQueries({ queryKey: NOTES_KEY });
  const failed = (e: Error) => toast.error(noteRefusal(e, t));

  const add = useMutation({
    // No day is left out rather than sent as null, which the API refused on a new line until 2026-10-15.
    mutationFn: (draft: TodoDraft) => api.post<TodoItem>('/notes/todos', { ...todoBody(draft), dueOn: draft.dueOn ?? undefined }),
    onSuccess: () => {
      setComposer((n) => n + 1);
      void refresh();
    },
    onError: failed,
  });

  const save = useMutation({
    mutationFn: ({ id, draft }: { id: string; draft: TodoDraft }) => api.patch<TodoItem>(`/notes/todos/${id}`, todoBody(draft)),
    onSuccess: () => {
      setEditing(null);
      void refresh();
    },
    onError: failed,
  });

  // Ticked on screen at once — it moves to Done — and put back if the API refuses.
  const tick = useMutation({
    mutationFn: ({ item, isDone }: { item: TodoItem; isDone: boolean }) =>
      api.patch<TodoItem>(`/notes/todos/${item.id}`, { isDone }),
    onMutate: async ({ item, isDone }) => {
      await queryClient.cancelQueries({ queryKey: NOTES_KEY });
      const previous = queryClient.getQueryData<NotesOverview>(NOTES_KEY);
      queryClient.setQueryData<NotesOverview>(NOTES_KEY, (old) =>
        old && {
          ...old,
          todos: old.todos.map((line) =>
            line.id === item.id ? { ...line, isDone, doneAt: isDone ? new Date().toISOString() : null } : line,
          ),
        },
      );
      return { previous };
    },
    onError: (e: Error, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(NOTES_KEY, context.previous);
      failed(e);
    },
    onSettled: refresh,
  });

  const remove = useMutation({
    mutationFn: (item: TodoItem) => api.del(`/notes/todos/${item.id}`),
    onSuccess: () => {
      toast.success(t('notes.todo.removed'));
      void refresh();
    },
    onError: failed,
  });

  const clear = useMutation({
    mutationFn: () => api.post<{ count: number }>('/notes/todos/clear-done'),
    onSuccess: () => {
      setConfirmClear(false);
      toast.success(t('notes.todo.cleared'));
      void refresh();
    },
    onError: (e: Error) => {
      setConfirmClear(false);
      failed(e);
    },
  });

  const busy = (item: TodoItem) =>
    (tick.isPending && tick.variables?.item.id === item.id) || (remove.isPending && remove.variables?.id === item.id);

  const dragWords = useDragWords(
    open.map((item) => item.id),
    (id) => {
      const item = open.find((line) => line.id === id);
      return item ? todoName(item, t) : '';
    },
  );

  const onDragEnd = (event: DragEndEvent) => {
    const order = droppedOrder(
      open.map((item) => item.id),
      event,
    );
    if (order) reorder.mutate(order);
  };

  return (
    <div className="max-w-3xl space-y-4">
      <Card>
        <TodoEditor
          key={composer}
          autoFocus={composer > 0}
          submitLabel={t('common.add')}
          busy={add.isPending}
          onSubmit={(draft) => add.mutate(draft)}
        />
      </Card>

      <Card padded={false}>
        {open.length === 0 ? (
          done.length > 0 ? (
            <p className="px-5 py-8 text-center text-body-sm text-muted">{t('notes.todo.allDone')}</p>
          ) : (
            <EmptyState
              icon={<ListTodo className="h-8 w-8" aria-hidden />}
              title={t('notes.todo.empty')}
              hint={t('notes.todo.emptyHint')}
            />
          )
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={onDragEnd}
            accessibility={dragWords}
          >
            <SortableContext items={open.map((item) => item.id)} strategy={verticalListSortingStrategy}>
              <ul>
                {open.map((item) => (
                  <TodoRow
                    key={item.id}
                    item={item}
                    busy={busy(item)}
                    editing={editing === item.id}
                    saving={save.isPending && save.variables?.id === item.id}
                    onEdit={() => setEditing(item.id)}
                    onCancelEdit={() => setEditing(null)}
                    onSave={(draft) => save.mutate({ id: item.id, draft })}
                    onTick={(isDone) => tick.mutate({ item, isDone })}
                    onRemove={() => remove.mutate(item)}
                  />
                ))}
              </ul>
            </SortableContext>
          </DndContext>
        )}
      </Card>

      {done.length > 0 && (
        <Card padded={false}>
          <div className="flex items-center justify-between gap-3 px-4 py-2">
            <button
              type="button"
              onClick={() => setShowDone((shown) => !shown)}
              aria-expanded={showDone}
              className="inline-flex items-center gap-1.5 rounded-md py-1 text-body-sm font-medium text-[var(--text-secondary)] hover:text-[var(--text-primary)]"
            >
              <ChevronDown
                className={clsx('h-4 w-4 transition-transform duration-fast', !showDone && '-rotate-90 rtl:rotate-90')}
                aria-hidden
              />
              {t('notes.todo.done')}
              <span className="text-faint tabular">{done.length}</span>
            </button>
            <Button size="sm" variant="ghost" icon={<Trash2 className="h-4 w-4" />} onClick={() => setConfirmClear(true)}>
              {t('notes.todo.clearDone')}
            </Button>
          </div>
          {showDone && (
            <ul className="border-t hairline">
              {done.map((item) => (
                <DoneRow
                  key={item.id}
                  item={item}
                  busy={busy(item)}
                  onTick={(isDone) => tick.mutate({ item, isDone })}
                  onRemove={() => remove.mutate(item)}
                />
              ))}
            </ul>
          )}
        </Card>
      )}

      {confirmClear && (
        <ConfirmDialog
          title={t('notes.todo.clearDoneConfirm')}
          confirmLabel={t('notes.todo.clearDone')}
          danger
          busy={clear.isPending}
          onConfirm={() => clear.mutate()}
          onCancel={() => setConfirmClear(false)}
        >
          <p>{t('notes.todo.clearDoneBody', { n: done.length })}</p>
        </ConfirmDialog>
      )}
    </div>
  );
}

/**
 * Writing a line — a new one, or one being changed: its words (where @ tags),
 * the day it is for, and its tags. Enter saves; Escape gives up a change.
 */
function TodoEditor({
  initial,
  submitLabel,
  busy,
  onSubmit,
  onCancel,
  autoFocus,
}: {
  initial?: TodoItem;
  submitLabel: string;
  busy: boolean;
  onSubmit: (draft: TodoDraft) => void;
  onCancel?: () => void;
  autoFocus?: boolean;
}) {
  const { t } = useI18n();
  const [title, setTitle] = useState(initial?.title ?? '');
  const [tags, setTags] = useState<NoteTag[]>(initial?.tags ?? []);
  const [dueOn, setDueOn] = useState(initial?.dueOn ?? '');
  const ready = (title.trim() !== '' || tags.length > 0) && !busy;
  const submit = () => {
    if (ready) onSubmit({ title: title.trim(), tags, dueOn: dueOn || null });
  };

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-start gap-2">
        <TagField
          className="min-w-0 grow basis-64"
          value={title}
          onChange={setTitle}
          tags={tags}
          onTagsChange={setTags}
          placeholder={t('notes.todo.placeholder')}
          label={t('notes.todo.label')}
          maxLength={TODO_TITLE_MAX}
          onSubmit={submit}
          onCancel={onCancel}
          autoFocus={autoFocus}
        />
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <DueField value={dueOn} onChange={setDueOn} />
          <Button
            variant="primary"
            icon={initial ? undefined : <Plus className="h-4 w-4" />}
            loading={busy}
            disabled={!ready}
            onClick={submit}
          >
            {submitLabel}
          </Button>
          {onCancel && (
            <Button variant="ghost" onClick={onCancel}>
              {t('common.cancel')}
            </Button>
          )}
        </div>
      </div>
      <TagChips tags={tags} onRemove={(tag) => setTags(tags.filter((kept) => !sameTag(kept, tag)))} />
    </div>
  );
}

/** The day a line is for: the browser's own date picker, and a × to clear it. */
function DueField({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  const { t } = useI18n();
  return (
    <span
      className={clsx(
        'inline-flex h-10 items-center gap-1 rounded-md border bg-[var(--surface-raised)] pe-1 ps-2.5 hairline',
        'transition-colors duration-fast focus-within:border-primary focus-within:ring-1 focus-within:ring-primary',
      )}
    >
      <CalendarDays className="h-4 w-4 shrink-0 text-faint" aria-hidden />
      <input
        type="date"
        value={value}
        min="2000-01-01"
        max="2100-12-31"
        onChange={(e) => onChange(e.target.value)}
        aria-label={t('notes.todo.dueLabel')}
        title={t('notes.todo.dueLabel')}
        dir="ltr"
        className="h-8 bg-transparent text-body-sm text-[var(--text-primary)] focus:outline-none focus-visible:shadow-none"
      />
      {value && (
        <button
          type="button"
          onClick={() => onChange('')}
          aria-label={t('notes.todo.clearDue')}
          title={t('notes.todo.clearDue')}
          className="grid h-7 w-7 place-items-center rounded-full text-faint hover:bg-[var(--surface-sunken)] hover:text-[var(--text-primary)]"
        >
          <X className="h-3.5 w-3.5" aria-hidden />
        </button>
      )}
    </span>
  );
}

/** A line's day, in the colour of where it stands. */
const DUE_TONE: Record<ReturnType<typeof dueState>, BadgeTone> = {
  overdue: 'error',
  today: 'warning',
  tomorrow: 'info',
  later: 'neutral',
};

/** Today, Tomorrow, Overdue · 12 Oct 2026, or the date — plain once the line is done. */
function DueBadge({ day, done = false }: { day: string; done?: boolean }) {
  const { t, locale } = useI18n();
  const date = formatDate(dayDate(day), locale);
  const state = done ? 'later' : dueState(day);
  const label =
    state === 'overdue'
      ? t('notes.todo.overdue', { date })
      : state === 'today'
        ? t('notes.todo.today')
        : state === 'tomorrow'
          ? t('notes.todo.tomorrow')
          : date;
  return (
    <Badge tone={DUE_TONE[state]} title={date}>
      <CalendarDays className="h-3 w-3" aria-hidden />
      {label}
    </Badge>
  );
}

function TodoRow({
  item,
  busy,
  editing,
  saving,
  onEdit,
  onCancelEdit,
  onSave,
  onTick,
  onRemove,
}: {
  item: TodoItem;
  busy: boolean;
  editing: boolean;
  saving: boolean;
  onEdit: () => void;
  onCancelEdit: () => void;
  onSave: (draft: TodoDraft) => void;
  onTick: (isDone: boolean) => void;
  onRemove: () => void;
}) {
  const { t } = useI18n();
  const sortable = useSortable({ id: item.id, disabled: editing });
  return (
    <li
      ref={sortable.setNodeRef}
      style={sortableStyle(sortable)}
      className={clsx(
        'border-b hairline last:border-b-0',
        sortable.isDragging && 'relative z-10 rounded-md bg-[var(--surface-raised)] shadow-lg',
      )}
    >
      {editing ? (
        <div className="px-4 py-3">
          <TodoEditor
            initial={item}
            submitLabel={t('common.save')}
            busy={saving}
            onSubmit={onSave}
            onCancel={onCancelEdit}
            autoFocus
          />
        </div>
      ) : (
        <div className="flex items-start gap-2 px-2 py-2.5 sm:px-3">
          <DragHandle label={t('notes.todo.drag')} sortable={sortable} />
          <input
            type="checkbox"
            checked={item.isDone}
            disabled={busy}
            onChange={(e) => onTick(e.target.checked)}
            aria-label={todoName(item, t)}
            className="mt-1.5 h-4 w-4 shrink-0 cursor-pointer accent-primary disabled:cursor-default"
          />
          <div className="min-w-0 flex-1 pb-0.5 pt-1">
            {item.title && (
              <p className="whitespace-pre-wrap break-words text-body-sm text-[var(--text-primary)]" dir="auto">
                {item.title}
              </p>
            )}
            {(item.tags.length > 0 || item.dueOn) && (
              <div className={clsx('flex min-w-0 flex-wrap items-center gap-1.5', item.title && 'mt-1.5')}>
                {item.dueOn && <DueBadge day={item.dueOn} />}
                {item.tags.map((tag) => (
                  <TagChip key={`${tag.kind}:${tag.id}`} tag={tag} />
                ))}
              </div>
            )}
          </div>
          <span className="flex shrink-0 items-center">
            <IconButton label={t('notes.todo.edit')} onClick={onEdit}>
              <Pencil className="h-3.5 w-3.5" aria-hidden />
            </IconButton>
            <IconButton label={t('notes.todo.remove')} onClick={onRemove} disabled={busy}>
              <Trash2 className="h-3.5 w-3.5" aria-hidden />
            </IconButton>
          </span>
        </div>
      )}
    </li>
  );
}

function DoneRow({
  item,
  busy,
  onTick,
  onRemove,
}: {
  item: TodoItem;
  busy: boolean;
  onTick: (isDone: boolean) => void;
  onRemove: () => void;
}) {
  const { t, locale } = useI18n();
  return (
    // Its box lines up with the open lines' boxes, past their grips.
    <li className="flex items-start gap-2 border-b py-2.5 pe-2 ps-10 hairline last:border-b-0 sm:pe-3 sm:ps-11">
      <input
        type="checkbox"
        checked={item.isDone}
        disabled={busy}
        onChange={(e) => onTick(e.target.checked)}
        aria-label={todoName(item, t)}
        className="mt-1.5 h-4 w-4 shrink-0 cursor-pointer accent-primary disabled:cursor-default"
      />
      <div className="min-w-0 flex-1 pb-0.5 pt-1">
        {item.title && (
          <p className="whitespace-pre-wrap break-words text-body-sm text-muted line-through" dir="auto">
            {item.title}
          </p>
        )}
        <div className={clsx('flex min-w-0 flex-wrap items-center gap-1.5', item.title && 'mt-1.5')}>
          {item.dueOn && <DueBadge day={item.dueOn} done />}
          {item.tags.map((tag) => (
            <TagChip key={`${tag.kind}:${tag.id}`} tag={tag} />
          ))}
          <span className="text-caption text-faint" title={formatDateTime(item.doneAt, locale)}>
            {t('notes.todo.doneWhen', { when: relativeTime(item.doneAt, locale) })}
          </span>
        </div>
      </div>
      <IconButton label={t('notes.todo.remove')} onClick={onRemove} disabled={busy}>
        <Trash2 className="h-3.5 w-3.5" aria-hidden />
      </IconButton>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Sticky notes
// ---------------------------------------------------------------------------

/** What a note is called where a screen reader names it: its first line, short, its tags, or its first picture. */
function noteName(note: StickyNoteItem, t: (key: TranslationKey) => string): string {
  const firstLine = noteLines(note.body)[0] ?? '';
  if (firstLine) return firstLine.length > 60 ? `${firstLine.slice(0, 60)}…` : firstLine;
  const tagged = note.tags.filter((tag) => tag.available).map((tag) => tag.number ?? tag.title);
  if (tagged.length) return tagged.join(', ');
  return note.images[0]?.originalName ?? t('notes.sticky.label');
}

/** Pinned first, then each in its place on the board. */
const byPinnedThenPlace = (a: StickyNoteItem, b: StickyNoteItem) =>
  Number(b.isPinned) - Number(a.isPinned) || byPlace(a, b);

/**
 * Changed after it was added. A note saves itself as it is written, its first
 * save making it, so the saves right after — while it is still being written —
 * are its writing, not an edit: it says "Added" for its first two minutes.
 */
const WRITING_MS = 2 * 60_000;
const editedLater = (note: StickyNoteItem) =>
  note.editedAt !== null && Date.parse(note.editedAt) - Date.parse(note.createdAt) > WRITING_MS;

function StickyBoard({ notes }: { notes: StickyNoteItem[] }) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  // A new composer after each note — empty, and holding the focus for the next one.
  const [composer, setComposer] = useState(0);
  // The next note takes the colour the last one was given.
  const [color, setColor] = useState<StickyColor>('AMBER');
  // The note the composer's first save made: it joins the board once it is done, not while it is written.
  const [composing, setComposing] = useState<string | null>(null);
  const shown = notes.filter((note) => note.id !== composing);
  const sorted = [...shown].sort(byPinnedThenPlace);
  const pinned = sorted.filter((note) => note.isPinned);
  const others = sorted.filter((note) => !note.isPinned);

  return (
    <div className="space-y-6">
      <div className={clsx('mx-auto max-w-2xl rounded-lg border p-4 shadow-sm', NOTE_STYLE[color].card)}>
        <NoteEditor
          key={composer}
          autoFocus={composer > 0}
          color={color}
          onColorChange={setColor}
          onCreated={(note) => setComposing(note.id)}
          onDone={() => {
            setComposer((n) => n + 1);
            setComposing(null);
            void queryClient.invalidateQueries({ queryKey: NOTES_KEY });
          }}
        />
      </div>

      {shown.length === 0 ? (
        <EmptyState
          icon={<StickyNote className="h-8 w-8" aria-hidden />}
          title={t('notes.sticky.empty')}
          hint={t('notes.sticky.emptyHint')}
        />
      ) : (
        <>
          {pinned.length > 0 && <NoteGroup title={t('notes.sticky.pinned')} notes={pinned} />}
          {others.length > 0 && <NoteGroup title={pinned.length > 0 ? t('notes.sticky.others') : null} notes={others} />}
        </>
      )}
    </div>
  );
}

/** The pinned notes, or the rest: a grid each note is dragged around in. */
function NoteGroup({ title, notes }: { title: string | null; notes: StickyNoteItem[] }) {
  const { t } = useI18n();
  const sensors = useDragSensors();
  const reorder = useReorder('notes');
  const ids = notes.map((note) => note.id);
  const dragWords = useDragWords(ids, (id) => {
    const note = notes.find((one) => one.id === id);
    return note ? noteName(note, t) : '';
  });
  return (
    <section>
      {title && <h2 className="mb-2 text-overline uppercase text-[var(--text-tertiary)]">{title}</h2>}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        accessibility={dragWords}
        onDragEnd={(event) => {
          const order = droppedOrder(ids, event);
          if (order) reorder.mutate(order);
        }}
      >
        <SortableContext items={ids} strategy={rectSortingStrategy}>
          <ul className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
            {notes.map((note) => (
              <NoteCard key={note.id} note={note} />
            ))}
          </ul>
        </SortableContext>
      </DndContext>
    </section>
  );
}

function NoteCard({ note }: { note: StickyNoteItem }) {
  const { t, locale } = useI18n();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draftColor, setDraftColor] = useState<StickyColor>(note.color);
  const [choosing, setChoosing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const sortable = useSortable({ id: note.id, disabled: editing });
  const refresh = () => queryClient.invalidateQueries({ queryKey: NOTES_KEY });

  // A pin or a colour, from the note's own buttons; its words are saved by the editor as they are written.
  const update = useMutation({
    mutationFn: (body: Record<string, unknown>) => api.patch<StickyNoteItem>(`/notes/stickies/${note.id}`, body),
    onSuccess: () => {
      setChoosing(false);
      void refresh();
    },
    onError: (e: Error) => toast.error(noteRefusal(e, t)),
  });

  const remove = useMutation({
    mutationFn: () => api.del(`/notes/stickies/${note.id}`),
    onSuccess: () => {
      setConfirmDelete(false);
      toast.success(t('notes.sticky.deleted'));
      void refresh();
    },
    onError: (e: Error) => {
      setConfirmDelete(false);
      toast.error(noteRefusal(e, t));
    },
  });

  const startEditing = () => {
    setDraftColor(note.color);
    setChoosing(false);
    setEditing(true);
  };

  return (
    <li
      ref={sortable.setNodeRef}
      style={sortableStyle(sortable)}
      className={clsx(
        'relative flex min-h-[10rem] min-w-0 flex-col rounded-lg border p-4 shadow-sm',
        NOTE_STYLE[editing ? draftColor : note.color].card,
        sortable.isDragging && 'z-10 shadow-xl',
      )}
    >
      {editing ? (
        <NoteEditor
          note={note}
          color={draftColor}
          onColorChange={setDraftColor}
          onDone={() => {
            setEditing(false);
            void refresh();
          }}
          autoFocus
        />
      ) : (
        <>
          <div className="flex items-start gap-2">
            {note.isPinned && (
              <Pin className="mt-1 h-3.5 w-3.5 shrink-0 text-primary-600 dark:text-primary-400" aria-label={t('notes.sticky.pinned')} />
            )}
            <div className="min-w-0 flex-1">
              <NoteImages images={note.images} className={note.body ? 'mb-3' : undefined} />
              <NoteText html={note.body} onDoubleClick={startEditing} />
            </div>
            <DragHandle label={t('notes.sticky.drag')} sortable={sortable} />
          </div>
          <TagChips tags={note.tags} className={note.body || note.images.length > 0 ? 'mt-3' : 'mt-1'} />
          <div className="mt-auto flex flex-wrap items-center justify-between gap-x-2 gap-y-1 pt-3">
            <span className="text-caption text-faint" title={formatDateTime(note.editedAt ?? note.createdAt, locale)}>
              {editedLater(note)
                ? t('notes.sticky.edited', { when: relativeTime(note.editedAt, locale) })
                : t('notes.sticky.added', { when: relativeTime(note.createdAt, locale) })}
            </span>
            <span className="-me-2 flex items-center">
              <IconButton
                label={note.isPinned ? t('notes.sticky.unpin') : t('notes.sticky.pin')}
                onClick={() => update.mutate({ isPinned: !note.isPinned })}
                disabled={update.isPending}
              >
                {note.isPinned ? <PinOff className="h-3.5 w-3.5" aria-hidden /> : <Pin className="h-3.5 w-3.5" aria-hidden />}
              </IconButton>
              <IconButton label={t('notes.sticky.color')} onClick={() => setChoosing((open) => !open)} expanded={choosing}>
                <Palette className="h-3.5 w-3.5" aria-hidden />
              </IconButton>
              <IconButton label={t('notes.sticky.edit')} onClick={startEditing}>
                <Pencil className="h-3.5 w-3.5" aria-hidden />
              </IconButton>
              <IconButton label={t('notes.sticky.delete')} onClick={() => setConfirmDelete(true)}>
                <Trash2 className="h-3.5 w-3.5" aria-hidden />
              </IconButton>
            </span>
          </div>
          {choosing && (
            <div className="mt-2 flex justify-end">
              <ColorChoice value={note.color} onChange={(color) => update.mutate({ color })} />
            </div>
          )}
        </>
      )}

      {confirmDelete && (
        <ConfirmDialog
          title={t('notes.sticky.deleteConfirm')}
          confirmLabel={t('common.delete')}
          danger
          busy={remove.isPending}
          onConfirm={() => remove.mutate()}
          onCancel={() => setConfirmDelete(false)}
        >
          <p>{t('notes.sticky.deleteBody')}</p>
        </ConfirmDialog>
      )}
    </li>
  );
}
